import React, { useState } from "react";
import { useNavigate, Link, useLocation, useSearchParams } from "react-router-dom";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import logo from "@/assets/logo.jpg";
import { Loader2, MailCheck, ArrowLeft } from "lucide-react";

const bg = "https://images.unsplash.com/photo-1455390582262-044cdead277a?crop=entropy&cs=srgb&fm=jpg&q=80&w=1400";

function AuthShell({ title, subtitle, children }) {
  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-brand-bg">
      <div className="hidden md:block relative">
        <img src={bg} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-brand-green/80" />
        <div className="relative h-full flex flex-col justify-between p-12 text-white">
          <img src={logo} alt="Rehoboth Wealth Management" className="h-12 w-auto object-contain bg-white/95 rounded-lg p-2 self-start" />
          <div>
            <h2 className="font-serif text-4xl leading-tight">Your legacy, thoughtfully arranged.</h2>
            <p className="text-white/70 mt-4 max-w-sm">Complete your Will Instruction securely online and track your application every step of the way.</p>
          </div>
          <p className="text-white/50 text-sm">Rehoboth Wealth Group</p>
        </div>
      </div>
      <div className="flex items-center justify-center p-6 md:p-10">
        <div className="w-full max-w-md animate-fade-up">
          <div className="md:hidden mb-8 flex justify-center"><img src={logo} alt="logo" className="h-12 object-contain" /></div>
          <h1 className="font-serif text-3xl text-brand-green">{title}</h1>
          <p className="text-brand-muted mt-1 mb-8">{subtitle}</p>
          {children}
        </div>
      </div>
    </div>
  );
}

function OtpStep({ challengeId, maskedEmail, onVerified }) {
  const { verifyOtp } = useAuth();
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await verifyOtp(challengeId, code.trim());
      onVerified(u);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  const resend = async () => {
    setResending(true);
    try {
      await api.post("/auth/resend-otp", { challenge_id: challengeId });
      toast.success("A new code has been sent to your email.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setResending(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-5" data-testid="otp-form">
      <div className="flex items-center gap-3 rounded-lg bg-brand-admin border border-brand-line p-3 text-sm text-brand-ink">
        <MailCheck className="w-5 h-5 text-brand-green shrink-0" />
        <span>We've emailed a 6-digit code to <strong>{maskedEmail}</strong>. Enter it below to continue.</span>
      </div>
      <div>
        <Label className="text-sm font-semibold text-brand-ink mb-1.5 inline-block">Verification code</Label>
        <Input inputMode="numeric" autoFocus maxLength={6} required value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
          placeholder="______"
          className="bg-white border-brand-line focus-visible:ring-brand-green tracking-[0.5em] text-center text-lg font-semibold"
          data-testid="otp-code-input" />
      </div>
      <Button type="submit" disabled={loading || code.length < 6} className="w-full bg-brand-green hover:bg-brand-greenHover text-white" data-testid="otp-verify-submit">
        {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Verify & continue
      </Button>
      <button type="button" onClick={resend} disabled={resending}
        className="text-sm text-brand-green font-semibold underline w-full text-center disabled:opacity-50" data-testid="otp-resend">
        {resending ? "Sending…" : "Didn't get a code? Resend"}
      </button>
    </form>
  );
}

export function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [challenge, setChallenge] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await login(email, password);
      if (res?.twofa_required) {
        setChallenge({ id: res.challenge_id, email: res.email });
        toast.success("Verification code sent to your email");
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  const onVerified = (u) => {
    toast.success("Welcome back");
    navigate(u.role === "admin" ? "/admin" : (location.state?.from?.pathname || "/dashboard"), { replace: true });
  };

  if (challenge) {
    return (
      <AuthShell title="Verify it's you" subtitle="Two-factor authentication keeps your account secure.">
        <OtpStep challengeId={challenge.id} maskedEmail={challenge.email} onVerified={onVerified} />
        <button onClick={() => setChallenge(null)} className="text-sm text-brand-muted mt-6 flex items-center gap-1 mx-auto hover:text-brand-green" data-testid="otp-back">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to sign in
        </button>
      </AuthShell>
    );
  }

  return (
    <AuthShell title="Client sign in" subtitle="Access your will instruction and dashboard.">
      <form onSubmit={submit} className="space-y-5">
        <div>
          <Label className="text-sm font-semibold text-brand-ink mb-1.5 inline-block">Email</Label>
          <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
            className="bg-white border-brand-line focus-visible:ring-brand-green" data-testid="login-email" />
        </div>
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <Label className="text-sm font-semibold text-brand-ink inline-block">Password</Label>
            <Link to="/forgot-password" className="text-xs text-brand-green font-semibold underline" data-testid="login-forgot-password">Forgot password?</Link>
          </div>
          <Input type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
            className="bg-white border-brand-line focus-visible:ring-brand-green" data-testid="login-password" />
        </div>
        <Button type="submit" disabled={loading} className="w-full bg-brand-green hover:bg-brand-greenHover text-white" data-testid="login-submit">
          {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Sign in
        </Button>
      </form>
      <p className="text-sm text-brand-muted mt-6 text-center">
        No account? <Link to="/register" className="text-brand-green font-semibold underline" data-testid="login-to-register">Create one</Link>
      </p>
    </AuthShell>
  );
}

export function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [challenge, setChallenge] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    setLoading(true);
    try {
      const res = await register(name, email, password);
      if (res?.twofa_required) {
        setChallenge({ id: res.challenge_id, email: res.email });
        toast.success("Verification code sent to your email");
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  const onVerified = () => {
    toast.success("Account verified");
    navigate("/dashboard", { replace: true });
  };

  if (challenge) {
    return (
      <AuthShell title="Verify your email" subtitle="Enter the code we sent to finish creating your account.">
        <OtpStep challengeId={challenge.id} maskedEmail={challenge.email} onVerified={onVerified} />
        <button onClick={() => setChallenge(null)} className="text-sm text-brand-muted mt-6 flex items-center gap-1 mx-auto hover:text-brand-green" data-testid="otp-back">
          <ArrowLeft className="w-3.5 h-3.5" /> Back
        </button>
      </AuthShell>
    );
  }

  return (
    <AuthShell title="Create your account" subtitle="Register with your email to begin your Will, LPA or Legacy Trust instruction.">
      <form onSubmit={submit} className="space-y-5">
        <div>
          <Label className="text-sm font-semibold text-brand-ink mb-1.5 inline-block">Full name</Label>
          <Input required value={name} onChange={(e) => setName(e.target.value)}
            className="bg-white border-brand-line focus-visible:ring-brand-green" data-testid="register-name" />
        </div>
        <div>
          <Label className="text-sm font-semibold text-brand-ink mb-1.5 inline-block">Email</Label>
          <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
            className="bg-white border-brand-line focus-visible:ring-brand-green" data-testid="register-email" />
        </div>
        <div>
          <Label className="text-sm font-semibold text-brand-ink mb-1.5 inline-block">Password</Label>
          <Input type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
            className="bg-white border-brand-line focus-visible:ring-brand-green" data-testid="register-password" />
        </div>
        <Button type="submit" disabled={loading} className="w-full bg-brand-green hover:bg-brand-greenHover text-white" data-testid="register-submit">
          {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Create account
        </Button>
      </form>
      <p className="text-sm text-brand-muted mt-6 text-center">
        Already registered? <Link to="/login" className="text-brand-green font-semibold underline" data-testid="register-to-login">Sign in</Link>
      </p>
    </AuthShell>
  );
}

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email, origin: window.location.origin });
      setSent(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <AuthShell title="Forgot your password?" subtitle="Enter your email and we'll send you a secure reset link.">
      {sent ? (
        <div className="space-y-5" data-testid="forgot-sent">
          <div className="flex items-center gap-3 rounded-lg bg-brand-admin border border-brand-line p-4 text-sm text-brand-ink">
            <MailCheck className="w-5 h-5 text-brand-green shrink-0" />
            <span>If an account exists for <strong>{email}</strong>, a password reset link has been sent. The link expires in 30 minutes.</span>
          </div>
          <Link to="/login" className="text-sm text-brand-green font-semibold underline flex items-center gap-1" data-testid="forgot-back-login">
            <ArrowLeft className="w-3.5 h-3.5" /> Back to sign in
          </Link>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-5">
          <div>
            <Label className="text-sm font-semibold text-brand-ink mb-1.5 inline-block">Email</Label>
            <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              className="bg-white border-brand-line focus-visible:ring-brand-green" data-testid="forgot-email" />
          </div>
          <Button type="submit" disabled={loading} className="w-full bg-brand-green hover:bg-brand-greenHover text-white" data-testid="forgot-submit">
            {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Send reset link
          </Button>
          <Link to="/login" className="text-sm text-brand-muted flex items-center gap-1 justify-center hover:text-brand-green" data-testid="forgot-back">
            <ArrowLeft className="w-3.5 h-3.5" /> Back to sign in
          </Link>
        </form>
      )}
    </AuthShell>
  );
}

export function ResetPassword() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    if (password !== confirm) { toast.error("Passwords do not match"); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      toast.success("Password reset — please sign in");
      navigate("/login", { replace: true });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  if (!token) {
    return (
      <AuthShell title="Invalid reset link" subtitle="This password reset link is missing or malformed.">
        <Link to="/forgot-password" className="text-sm text-brand-green font-semibold underline" data-testid="reset-request-new">Request a new reset link</Link>
      </AuthShell>
    );
  }

  return (
    <AuthShell title="Choose a new password" subtitle="Enter and confirm your new password below.">
      <form onSubmit={submit} className="space-y-5">
        <div>
          <Label className="text-sm font-semibold text-brand-ink mb-1.5 inline-block">New password</Label>
          <Input type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
            className="bg-white border-brand-line focus-visible:ring-brand-green" data-testid="reset-password" />
        </div>
        <div>
          <Label className="text-sm font-semibold text-brand-ink mb-1.5 inline-block">Confirm password</Label>
          <Input type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)}
            className="bg-white border-brand-line focus-visible:ring-brand-green" data-testid="reset-confirm" />
        </div>
        <Button type="submit" disabled={loading} className="w-full bg-brand-green hover:bg-brand-greenHover text-white" data-testid="reset-submit">
          {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Reset password
        </Button>
      </form>
    </AuthShell>
  );
}
